/**
 * The badge for one sample pin on the sample group map.
 *
 * Deliberately the same object as the inspection point marker in the maps
 * module, `maps/utils/inspect-point-marker.ts`: a 22px circle in the brand
 * primary, white text, a white ring and a soft drop shadow. The two screens sit
 * next to each other in the product and a sample pin that looked like a
 * different kind of thing would read as one.
 *
 * It is a separate function rather than a call into that file because the two
 * differ in what they carry. An inspection point is numbered by its position in
 * a temporary list, so `index + 1` is the whole label, it is short, and it fits
 * inside the circle. A sample carries a user-typed name that is not a sequence,
 * is not guaranteed short and is not guaranteed unique, so its name hangs below
 * the circle instead and the circle itself stays a plain dot. That also removes
 * any limit on the name: the label below can be any length without changing the
 * size of the marker or the position it points at.
 *
 * The teardrop pin images this replaced (`assets/icons/*_pin.png`, 24x29) also
 * carried the pin type. A sample group is samples, so the type is near enough
 * uniform in practice, but it is no longer visible: it moves to the title.
 */

/** Matches the inspection point marker so the two read as one family. */
const SIZE = 22;
const RING = '0 0 0 2px #fff,0 1px 4px rgba(0,0,0,.4)';

/**
 * The selected badge.
 *
 * The same treatment the expanded analysis dialog uses for its highlighted point
 * (`maps/pin-analysis-history/pin-analysis-expand.component.ts`), so selection
 * means the same thing on both maps. The outer ring is the primary at low alpha,
 * written as a literal because it sits inside a cssText string where a CSS
 * variable in a shadow colour cannot be mixed.
 */
const SELECTED_SHADOW = '0 0 0 3px #fff,0 0 0 6px rgba(226,112,58,.45),0 2px 8px rgba(0,0,0,.5)';

/**
 * Centre the badge on its coordinate.
 *
 * An advanced marker anchors the bottom centre of its content on the position,
 * which is right for a teardrop pin whose tip is the point, and wrong for a
 * circle whose centre is. Without this the badge floats half its height above
 * the sample, and because the overlap rings are drawn at the true centres, a
 * clustered badge would sit visibly off-centre inside its own ring. That ring
 * is the reason this map exists, so the two have to agree.
 *
 * The maps module's inspection point is centred too, so the two still anchor
 * the same way. It uses the standalone translate property rather than
 * transform, because the expanded analysis dialog highlights a point by writing
 * transform: scale() straight onto it (PinAnalysisExpandComponent.setHighlight),
 * which would wipe a transform.
 *
 * It is applied to the wrapper, which is sized to the circle alone. The label
 * hangs outside that box, so it does not drag the anchor down with it.
 */
const CENTRE_ON_POINT = 'translateY(50%)';

export type SampleLabelMode = 'name' | 'both' | 'none';

/**
 * What the badge says.
 *
 * Every sample on the pin, because one pin can carry more than one sample and
 * two badges stacked on one coordinate would misreport how many pins are there.
 * Blank names are dropped rather than rendered as an empty segment:
 * `pin_samples.name` is whatever the client posted, and when a group is created
 * with `isSequential` false every sample in it gets the same name, so duplicates
 * and blanks are both normal here.
 *
 * Exported and pure so the joining rules can be tested without a DOM.
 */
export function sampleBadgeText(names: (string | null | undefined)[], mode: SampleLabelMode): string {
  if (mode === 'none') return '';
  return names
    .map(name => (name ?? '').trim())
    .filter(name => name.length > 0)
    .join(' / ');
}

/**
 * One badge element.
 *
 * A DOM node can only be in one place at a time, so every marker needs its own
 * element and callers must not share one between positions.
 *
 * Width is `min-width` plus padding rather than a fixed box, which is what lets
 * one rule serve both shapes: a short name sits inside the 22px minimum and
 * stays a circle, a longer one grows the box into a pill of the same height and
 * corner radius. There is no character count to keep in step with the font.
 */
export function buildSamplePinMarker(text: string, title: string, selected = false): HTMLElement {
  // The wrapper is the circle's own box and nothing more. The label is taken out
  // of flow below it, so a long name cannot grow the marker, shift its centre or
  // change where it sits on the ground.
  const wrapper = document.createElement('div');
  wrapper.setAttribute('title', title);
  wrapper.style.cssText =
    `position:relative;width:${SIZE}px;height:${SIZE}px;cursor:pointer;` +
    `transform:${CENTRE_ON_POINT};`;

  const dot = document.createElement('div');
  dot.setAttribute('data-role', 'dot');
  dot.style.cssText =
    `width:${SIZE}px;height:${SIZE}px;border-radius:99px;background:var(--primary-dark);` +
    `box-shadow:${selected ? SELECTED_SHADOW : RING};` +
    (selected ? 'transform:scale(1.25);' : '') +
    'transition:transform .15s ease,box-shadow .15s ease';
  wrapper.appendChild(dot);

  if (text) {
    const label = document.createElement('div');
    // The chip that hangs under saved pins on the maps screens (`buildPinBadge`
    // in `maps/utils/pin-badge-marker.ts`), copied rather than reinterpreted,
    // down to the 0.55 alpha and the 6px radius. The field name chips are the
    // same object, so a sample name now reads as the same kind of thing as every
    // other name on a map in this product. No font-family for the same reason:
    // that chip inherits the app face, and so does this one.
    //
    // **No max-width and no truncation, deliberately.** A long name makes a wide
    // chip, and a dense group will collide; that is accepted for now. Truncating
    // from the end would hide exactly the characters that tell two samples apart
    // (`N509001` and `N509024` both become `N50...`), which is the wrong half to
    // lose. Bounding it properly means stripping the group's shared prefix and
    // placing only labels that fit, which is its own change: see the L2 and L5
    // strategies in `prototypes/pin-display-styles.html`.
    label.style.cssText =
      'position:absolute;top:100%;left:50%;transform:translateX(-50%);margin-top:2px;' +
      'white-space:nowrap;pointer-events:none;' +
      'font-size:11px;font-weight:600;color:#fff;background:rgba(0,0,0,0.55);' +
      'padding:2px 6px;border-radius:6px;';
    label.textContent = text;
    wrapper.appendChild(label);
  }

  return wrapper;
}

/** Selection without rebuilding the marker, so the map does not flicker on click. */
export function paintSamplePinSelection(el: HTMLElement, selected: boolean): void {
  // The wrapper owns the position, the dot owns the appearance, so scaling the
  // dot grows the marker without moving the point it marks.
  const dot = el.querySelector('[data-role="dot"]') as HTMLElement | null;
  if (!dot) return;
  dot.style.boxShadow = selected ? SELECTED_SHADOW : RING;
  dot.style.transform = selected ? 'scale(1.25)' : '';
}
